// Vue 3 i18n glue for MetaShip microfrontends.
//
// The host (legacy-client) owns the locale: it persists user.preferredLocale
// and broadcasts changes through window.MetaShipGlobalState. Modules only
// read. Consume via the @i18n Vite alias — see shared/i18n/README.md.
import { onBeforeUnmount, onMounted, ref, type App, type Ref } from "vue";
import { createI18n } from "vue-i18n";

type GlobalState = { locale?: string; [key: string]: unknown };
type MetaShipGlobalState = {
  state?: GlobalState;
  subscribe?: (cb: (s: GlobalState) => void) => () => void;
};

// No `declare global` here — modules ship their own Window.MetaShipGlobalState
// declarations (e.g. src/types/globals.d.ts) and a second one would collide.
function globalState(): MetaShipGlobalState | undefined {
  // Node / vitest (no DOM): same guard as vue2.js. createModuleI18n() runs at
  // import time when format helpers pull in plugins/i18n.ts — do not throw.
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { MetaShipGlobalState?: MetaShipGlobalState })
    .MetaShipGlobalState;
}

export const FALLBACK_LOCALE = "en";

export function currentLocale(): string {
  return globalState()?.state?.locale ?? FALLBACK_LOCALE;
}

/**
 * Create a vue-i18n instance pre-wired to the host's locale broadcast.
 * Pass the module's messages, typically eagerly imported by Vite:
 *
 *   const messages = Object.fromEntries(
 *     Object.entries(import.meta.glob("./locales/*.json", { eager: true }))
 *       .map(([p, m]) => [p.match(/([\w-]+)\.json$/)![1], (m as any).default])
 *   );
 *   app.use(createModuleI18n(messages));
 */
/**
 * Deliberately simplified public shape — vue-i18n's schema-generic `t` causes
 * TS2589 ("type instantiation is excessively deep") when called from plain .ts
 * services. Components are unaffected (useI18n() has its own typing).
 */
export interface ModuleI18n {
  global: {
    locale: Ref<string>;
    t: (key: string, params?: Record<string, unknown> | unknown[] | number) => string;
  };
  install: (app: App) => void;
}

export function createModuleI18n(
  messages: Record<string, Record<string, unknown>>
): ModuleI18n {
  const i18n = createI18n({
    legacy: false,
    locale: currentLocale(),
    fallbackLocale: FALLBACK_LOCALE,
    missingWarn: false,
    fallbackWarn: false,
    messages: messages as never,
  });
  globalState()?.subscribe?.((s) => {
    const next = s.locale ?? FALLBACK_LOCALE;
    if (i18n.global.locale.value !== next) {
      i18n.global.locale.value = next;
    }
  });
  return i18n as unknown as ModuleI18n;
}

/**
 * Reactive read-only ref of the host locale, for components that need the
 * raw value (date formatting, AG Grid localeText) rather than $t().
 * Same shape as the existing useDarkMode composables.
 */
export function useLocale(): Ref<string> {
  const locale = ref<string>(currentLocale());
  let unsubscribe: (() => void) | null = null;

  onMounted(() => {
    locale.value = currentLocale();
    unsubscribe =
      globalState()?.subscribe?.((s) => {
        locale.value = s.locale ?? FALLBACK_LOCALE;
      }) ?? null;
  });

  onBeforeUnmount(() => {
    unsubscribe?.();
    unsubscribe = null;
  });

  return locale;
}
